import { Stack } from '@mui/material';

import { useKaiTiFontStyles } from '../font/KaiTiFont';

// Mirrors _chinese_char_pattern in src/main/python/pinyin.py. The backend builds the pinyin list
// from exactly these characters (punctuation excluded), so this test has to match it or the pinyin
// pointer below desyncs from the text.
const chineseCharRegex = /[\u3007\u3400-\u4dbf\u4e00-\u9fff\ue815-\ue864\uf900-\ufaff]/;
// non-breaking space, so a column without pinyin still occupies the pinyin row and the characters
// stay aligned with each other
const blankPinyin = '\u00a0';

export interface WordPinyinProps {
    text: string | null | undefined;
    pinyinList: string[] | null | undefined; // one entry per Chinese character, punctuation excluded
    showCharacter?: boolean;
    pinyinFontSize?: string;
    characterFontSize?: string;
}

/**
 * Renders text as pinyin-above-character columns.
 *
 * pinyinList is indexed by Chinese character, not by position in text, so the pointer only advances
 * on Chinese characters. Punctuation gets a narrow column with no pinyin when characters are shown,
 * and is skipped entirely in pinyin-only mode.
 */
const WordPinyin = ({ text, pinyinList, showCharacter = true, pinyinFontSize = '32px', characterFontSize = '48px' }: WordPinyinProps) => {

    const kaiTiFontClass = useKaiTiFontStyles();

    if (!text) return null;

    let pinyinIndex = 0; // advances only on Chinese characters

    return (<>
        {Array.from(text).map((char, index) => {
            const isChinese = chineseCharRegex.test(char);
            let pinyinForChar = blankPinyin;
            if (isChinese) {
                pinyinForChar = pinyinList && pinyinList[pinyinIndex] ? pinyinList[pinyinIndex] : blankPinyin;
                pinyinIndex++;
            } else if (!showCharacter) {
                return null; // punctuation has no pinyin, so there is nothing to show
            }

            return (
                <Stack key={index} spacing={1} alignItems="center">
                    <span
                        style={{
                            marginLeft: isChinese ? '0.5em' : 0,
                            marginRight: isChinese ? '0.5em' : 0,
                            fontSize: pinyinFontSize,
                        }}>
                        {pinyinForChar}
                    </span>
                    {showCharacter && (
                        <div className={kaiTiFontClass.customFont} style={{ fontSize: characterFontSize, margin: 0, padding: 0, lineHeight: 0.9 }} >
                            <span>{char}</span>
                        </div>
                    )}
                </Stack>
            );
        })}
    </>);
};

export default WordPinyin;
